import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { SubscriptionTabDefinition } from '@mm/state/helpers/billing-subscription-tab-helpers';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import CompanyModel from '@mm/state/model/CompanyModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';
import { ActivePlanDetails } from './ActivePlanDetails/ActivePlanDetails';
import './ActiveSubscriptionTab.pcss';
import BillingPageCard from './BillingPageCard/BillingPageCard';
import PromoCodesList from './PromoCodesList/PromoCodesList';

type Props = {
  planTab: SubscriptionTabDefinition;
  searchInput: string;
  setSearchInput: (searchInput: string) => void;
};

export const ActiveSubscriptionTab = (props: Props) => {
  const { planTab, searchInput, setSearchInput } = props;
  const { pages, showPromotionsList } = planTab;
  const company = useSelector(activeCompanySelector);
  const promotions = useSelector(
    (state: StoreState) => state.promotions.entities,
  );

  const { getPriceForNextLevel } = useSubscriptionCRUDActions();
  const { attachPagesTeam } = useConnectedPageCRUDActions();
  const loadPricesForNextLevel = (subscriptionId: number, level: number) =>
    getPriceForNextLevel({
      subscriptionId,
      nextLevel: level,
    });
  const { openGeneralConfirmModal } = useModalActions();

  const openRemoveFromTeamModal = (
    company: CompanyModel | undefined,
    page: EnhancedBillingPage,
  ) => {
    openGeneralConfirmModal({
      headerText:
        'Are you sure you want to remove this page from your Agency subscription?',
      messageText:
        'This page will lose access to all Startup features once you remove it from your subscription.',
      cancelButtonFirst: true,
      okButtonText: 'Remove',
      onCloseHandler: ok => {
        if (ok && page.subscription && company) {
          const subscriptionId = page.subscription.id;
          const remainingPageIds = pages
            .filter(
              p =>
                p.subscription &&
                p.subscription.id === subscriptionId &&
                p.id !== page.id,
            )
            .map(p => p.id);
          attachPagesTeam(company.id, subscriptionId, remainingPageIds);
        }
      },
    });
  };

  return (
    <div
      className={classNames('active-subscription-tab', {
        'active-subscription-tab-team-layout': planTab.teamSubscription,
      })}
    >
      <ActivePlanDetails
        planTab={planTab}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        loadPricesForNextLevel={loadPricesForNextLevel}
      />

      <div
        className={classNames('active-subscription-tab__pages-area', {
          'active-subscription-tab__pages-area-team-subscription':
            planTab.teamSubscription,
        })}
      >
        {pages.map(page => (
          <BillingPageCard
            key={`${page.id} + ${
              page.subscription && page.subscription.maxSendAllotment
            }`}
            page={page}
            openRemoveFromTeamModal={() =>
              openRemoveFromTeamModal(company, page)
            }
            promotions={promotions}
            planTab={planTab}
            loadPricesForNextLevel={loadPricesForNextLevel}
          />
        ))}
      </div>

      {showPromotionsList && <PromoCodesList promotions={promotions} />}
    </div>
  );
};
